import React, { useState, useEffect, useRef, useContext } from 'react'
import { useNavigate } from 'react-router-dom'
import { DataTable } from 'primereact/datatable'
import { Button } from 'primereact/button'
import { Column } from 'primereact/column'
import { Toolbar } from 'primereact/toolbar'
import { Toast } from 'primereact/toast'
import { Tooltip } from 'primereact/tooltip'

// Import system logos
import originsDirect from '../../../assets/images/ORIGINS-DIRECT.png'
import originsManganegment from '../../../assets/images/ORIGINS-MANAGEMENT.png'
import originsDirectInstant from '../../../assets/images/ORIGINS-DIRECT-INSTANT.png'

import MainContentLayout from '../../Layout/MainContentLayout'
import {
  formatCurrency,
  formatDate,
  formatPercentage,
  ToastPopUp,
} from '../../shared/utils'
import server from '../../../server'
import {
  DateFilterTemplate,
  DropDownTemplate,
  InputFieldTemplate,
  MinMaxTemplate,
  MultiSelectTemplate,
  SingleDateFilterTemplate,
} from '../../shared/FilterTemplates'
import TransactionPopup from './transactionPopup'
import { AuthenticationContext } from '../../../Auth/authentication.context'
import useTableRowsPerPage from '../../../hooks/useTableRowsPerPage'
import { getInitialPaginationState } from '../../../shared/tablePagination'

// //theme
import 'primereact/resources/themes/lara-light-indigo/theme.css'
// //core
import 'primereact/resources/primereact.min.css'
// //icons
import 'primeicons/primeicons.css'

export default function PaPayables() {
  const dt = useRef()
  const toast = useRef(null)
  const { user } = useContext(AuthenticationContext)
  const { defaultRows, rowsPerPageOptions, applyPageEvent } = useTableRowsPerPage()

  const [orders, setOrders] = useState()
  const [tableLoading, setTableLoading] = useState(true)
  const [totalRecords, setTotalRecords] = useState(0)
  const [selectedOrder, setSelectedOrder] = useState(null)
  const [clearBtnDisabled, setClearBtnDisabled] = useState(true)
  const [dates, setDates] = useState(null)
  const [csvLoading, setcsvLoading] = useState(false)
  const [paymentStatusCounts, setPaymentStatusCounts] = useState({
    Pending: 0,
    Paid: 0,
    Cancelled: 0,
  })
  const [paymentModalVisible, setPaymentModalVisible] = useState(false)
  const [selectedPayment, setSelectedPayment] = useState(null)
  const [filteredTotals, setFilteredTotals] = useState(null)

  const navigate = useNavigate()
  const [id, setId] = useState(null)
  const [lazyState, setLazyState] = useState(() => getInitialPaginationState(user))

  useEffect(() => {
    setLazyState((prev) => ({ ...prev, rows: defaultRows }))
  }, [defaultRows])
  const [startDate, setStartDate] = useState(null)
  const [endDate, setEndDate] = useState(null)

  const initLazyState = () => {
    setLazyState((prevState) => ({
      ...prevState,
      sortField: '',
      sortOrder: '',
      filters: {
        supplierInvoiceNo: { value: '' },
        pharmacyName: { value: '' },
        manufacturerName: { value: '' },
        originalValue: { value: '' }, // This will hold min/max values
        confirmedExtendTotal: { value: '' }, // This will hold min/max values
        confirmedPaValue: { value: '' }, // This will hold min/max values
        estimatedPA: { value: '' }, // This will hold min/max values for estimated PA
        paymentStatus: { value: '' },
        paPaymentId: { value: '' },
        system: { value: '' }, // Add system filter
        updatedAt: { value: '' },
      },
    }))
  }

  // const onRowSelect = (event) => {
  //   const order = orders.find((item) => item.id === event.data.id)
  //   navigate(`${event.data.id}`, { state: order })
  // }

  const getData = () => {
    const currentDate = new Date()

    const orderNo = lazyState?.filters?.supplierInvoiceNo?.value
    const pharmacyName = lazyState?.filters?.pharmacyName?.value
    const manufacturerName = lazyState?.filters?.manufacturerName?.value
    const paymentStatus = lazyState?.filters?.paymentStatus?.value
    const originalValue = lazyState?.filters?.originalValue?.value
    const confirmedValue = lazyState?.filters?.confirmedExtendTotal?.value
    const paAmount = lazyState?.filters?.confirmedPaValue?.value
    const estimatedPA = lazyState?.filters?.estimatedPA?.value
    const system = lazyState?.filters?.system?.value

    // Use ISO 8601 format for dates instead of custom formatDate
    let dateQueryParams = ''
    if (startDate) dateQueryParams += `&startDate=${startDate.toISOString()}`
    if (endDate) dateQueryParams += `&endDate=${endDate.toISOString()}`

    const requestParams =
      `?page=${lazyState?.page ? lazyState.page + 1 : 1}` +
      `&limit=${lazyState?.rows}` +
      dateQueryParams +
      `${orderNo ? '&supplierInvoiceNo=' + encodeURIComponent(orderNo) : ''}` +
      `${pharmacyName ? '&pharmacyName=' + encodeURIComponent(pharmacyName) : ''}` +
      `${manufacturerName ? '&manufacturerName=' + encodeURIComponent(manufacturerName) : ''}` +
      `${paymentStatus ? '&paymentStatus=' + paymentStatus : ''}` +
      `${system ? '&system=' + system : ''}` +
      `${originalValue?.min ? '&minOriginalValue=' + originalValue.min : ''}` +
      `${originalValue?.max ? '&maxOriginalValue=' + originalValue.max : ''}` +
      `${
        confirmedValue?.min ? '&minConfirmedValue=' + confirmedValue.min : ''
      }` +
      `${
        confirmedValue?.max ? '&maxConfirmedValue=' + confirmedValue.max : ''
      }` +
      `${paAmount?.min ? '&minPaAmount=' + paAmount.min : ''}` +
      `${paAmount?.max ? '&maxPaAmount=' + paAmount.max : ''}` +
      `${estimatedPA?.min ? '&minEstimatedPA=' + estimatedPA.min : ''}` +
      `${estimatedPA?.max ? '&maxEstimatedPA=' + estimatedPA.max : ''}` +
      `${lazyState?.sortField ? '&sortBy=' + lazyState.sortField : ''}` +
      `${
        lazyState?.sortOrder
          ? '&sortOrder=' + (lazyState.sortOrder === 1 ? 'ASC' : 'DESC')
          : ''
      }`

    setTableLoading(true)

    server
      .get(`/admins/payables${requestParams}`)
      .then((response) => {
        const payablesData = response.data.data.payables
        console.log(payablesData)
        setOrders(payablesData)
        setTotalRecords(response.data.data.totalItems)
        setPaymentStatusCounts(response.data.data.paymentStatusCounts)
        setFilteredTotals(response.data.data.filteredTotals ?? null)
        setTableLoading(false)
      })
      .catch((error) => {
        setTableLoading(false)
      })
  }

  useEffect(() => {
    checkClearBtn()
    return getData()
  }, [lazyState, startDate, endDate])

  useEffect(() => initLazyState(), [])

  const onPage = (event) => {
    setLazyState((prev) => applyPageEvent(prev, event))
  }

  const onSort = (event) => {
    setLazyState((prev) => applyPageEvent(prev, event))
  }

  const onFilter = (event) => {
    setLazyState((prev) => applyPageEvent(prev, event))
  }

  // BODY TEMPLATES
  const originalValueBodyTemplate = (payable) =>
    formatCurrency(payable.originalValue)

  const confirmedValueBodyTemplate = (payable) =>
    formatCurrency(payable.confirmedExtendTotal)

  const paAmountBodyTemplate = (payable) =>
    formatCurrency(payable.confirmedPaValue)

  // System body template for displaying system logos
  const systemBodyTemplate = (payable) => {
    if (!payable.system || payable.system === 'Origins Direct') {
      return (
        <div className='d-flex justify-content-center'>
          <img
            src={originsDirect}
            alt='Origins Direct'
            style={{ height: '20px' }}
          />
        </div>
      )
    } else if (payable.system === 'Origins Direct Instant') {
      return (
        <div className='d-flex justify-content-center'>
          <img
            src={originsDirectInstant}
            alt='Origins Direct Instant'
            style={{ height: '20px' }}
          />
        </div>
      )
    } else if (payable.system === 'Origins Management') {
      return (
        <div className='d-flex justify-content-center'>
          <img
            src={originsManganegment}
            alt='Origins Management'
            style={{ height: '20px' }}
          />
        </div>
      )
    } else {
      return <span>{payable.system}</span>
    }
  }

  const statusBodyTemplate = (rowData) => {
    let statusColor
    let textColor
    const status = rowData.paymentStatus

    switch (status) {
      case 'Cancelled':
        statusColor = '#EF4444'
        textColor = '#EF4444'
        break
      case 'Pending':
        statusColor = '#86888b'
        textColor = '#86888b'
        break
      case 'Paid':
        statusColor = '#22c55e'
        textColor = '#22c55e'
        break
      default:
        statusColor = '#86888b'
        textColor = '#86888b'
    }

    return (
      <span
        className='d-flex justify-content-center py-1 rounded-pill'
        style={{ border: `1px solid ${statusColor}`, color: `${textColor}` }}
      >
        {status}
      </span>
    )
  }

  // Filter TEMPLATES
  const orderNoFilterElement = (options) => (
    <InputFieldTemplate
      state={options.value}
      stateHandler={(e) => options.filterCallback(e, options.index)}
      placeholder='Order No.'
    />
  )

  const pharmacyNameFilterElement = (options) => (
    <InputFieldTemplate
      state={options.value}
      stateHandler={(e) => options.filterCallback(e, options.index)}
      placeholder='Pharmacy name'
    />
  )

  const manufacturerFilterElement = (options) => (
    <InputFieldTemplate
      state={options.value}
      stateHandler={(e) => options.filterCallback(e, options.index)}
      placeholder='Manufacturer'
    />
  )

  // System filter element
  const systemFilterElement = (options) => (
    <DropDownTemplate
      state={options.value}
      stateHandler={(e) => options.filterCallback(e.value, options.index)}
      placeholder='Select System'
      options={[
        { name: 'Origins Direct', value: 'Origins Direct' },
        { name: 'Origins Direct Instant', value: 'Origins Direct Instant' },
        { name: 'Origins Management', value: 'Origins Management' },
      ]}
    />
  )

  const statusFilterElement = (options) => (
    <DropDownTemplate
      state={options.value}
      stateHandler={(e) => options.filterCallback(e.value, options.index)}
      placeholder='Payment Status'
      options={[
        { name: 'Pending', value: 'Pending' },
        { name: 'Paid', value: 'Paid' },
        { name: 'Cancelled', value: 'Cancelled' },
      ]}
    />
  )

  const originalValueFilterElement = (options) => (
    <MinMaxTemplate
      state={options.value}
      stateHandler={(e) => options.filterCallback(e, options.index)}
      placeholder='Original Value'
    />
  )

  const confirmedValueFilterElement = (options) => (
    <MinMaxTemplate
      state={options.value}
      stateHandler={(e) => options.filterCallback(e, options.index)}
      placeholder='Confirmed Value'
    />
  )

  const estimatedPABodyTemplate = (payable) =>
    formatCurrency(payable.estimatedPA)

  const paAmountFilterElement = (options) => (
    <MinMaxTemplate
      state={options.value}
      stateHandler={(e) => options.filterCallback(e, options.index)}
      placeholder='PA Amount'
    />
  )

  const estimatedPAFilterElement = (options) => (
    <MinMaxTemplate
      state={options.value}
      stateHandler={(e) => options.filterCallback(e, options.index)}
      placeholder='Estimated PA Amount'
    />
  )

  const checkClearBtn = () => {
    if (lazyState?.sortField || lazyState?.sortOrder) {
      setClearBtnDisabled(false)
      return
    }
    for (const key in lazyState?.filters) {
      if (lazyState?.filters[key].value) {
        setClearBtnDisabled(false)
        return
      }
    }
    setClearBtnDisabled(true)
  }

  // TOOLBARS
  const topRightToolbarTemplate = () => (
    <>
      <div className='d-flex m-3 align-items-center'>
        <span
          className='p-2 m-2 rounded-pill'
          style={{
            backgroundColor: '#86888b20',
            border: '1px solid #86888b',
            color: '#86888b',
          }}
        >
          Pending: {paymentStatusCounts.Pending || 0}
        </span>
        <span
          className='p-2 m-2 rounded-pill'
          style={{
            backgroundColor: '#22c55e20',
            border: '1px solid #22c55e',
            color: '#22c55e',
          }}
        >
          Paid: {paymentStatusCounts.Paid || 0}
        </span>
        <span
          className='p-2 m-2 rounded-pill'
          style={{
            backgroundColor: '#EF444420',
            border: '1px solid #EF4444',
            color: '#EF4444',
          }}
        >
          Cancelled: {paymentStatusCounts.Cancelled || 0}
        </span>
        <div className='ms-3'>
          <DropDownTemplate
            state={lazyState?.filters?.system?.value || null}
            stateHandler={(e) =>
              setLazyState((prev) => ({
                ...prev,
                filters: {
                  ...prev.filters,
                  system: {
                    ...(prev.filters?.system || {}),
                    value: e.value || '',
                  },
                },
              }))
            }
            optionValue='value'
            filter={false}
            placeholder='All Systems'
            options={[
              { name: 'Origins Direct', value: 'Origins Direct' },
              { name: 'Origins Direct Instant', value: 'Origins Direct Instant' },
              { name: 'Origins Management', value: 'Origins Management' },
            ]}
          />
        </div>
        <div className='d-flex gap-1 align-items-center ms-3'>
          <SingleDateFilterTemplate
            state={startDate}
            stateHandler={(e) => setStartDate(e.value)}
            placeholder='From'
            showIcon={false}
          />
          <SingleDateFilterTemplate
            state={endDate}
            stateHandler={(e) => setEndDate(e.value)}
            placeholder='To'
            disabled={!startDate}
            minDate={startDate}
          />
        </div>
      </div>
    </>
  )

  const leftToolbarTemplate = () => (
    <div className='d-flex align-items-center'>
      <Button
        type='button'
        icon='pi pi-filter-slash'
        label='Clear'
        outlined
        onClick={initLazyState}
        disabled={clearBtnDisabled}
        className='mr-2'
      />
    </div>
  )

  const actionBodyTemplate = (rowData) => {
    const isCancelled = rowData.paymentStatus === 'Cancelled'
    const isPaid = rowData.paymentStatus === 'Paid'

    if (isPaid) {
      return (
        <Button
          icon='pi pi-eye'
          rounded
          className='mr-2'
          onClick={() => handleViewPayment(rowData)}
          tooltip='View payment details'
          tooltipOptions={{ position: 'top' }}
        />
      )
    }

    return (
      <Button
        icon='pi pi-credit-card'
        rounded
        className='mr-2'
        onClick={(e) => handlePaymentAction(e, rowData)}
        disabled={isCancelled}
        tooltip={
          isCancelled
            ? 'Cancelled payments cannot be modified'
            : 'Update payment'
        }
        tooltipOptions={{ position: 'top' }}
      />
    )
  }

  // Updated function to handle the payment action
  const handlePaymentAction = (e, rowData) => {
    e.preventDefault()
    e.stopPropagation() // Prevent row selection when clicking the button
    setSelectedPayment(rowData)
    setId(rowData.id)
    setPaymentModalVisible(true)
  }

  // Function to handle saving payment updates
  const handlePaymentSave = (updatedDataFromServer) => {
    console.log('Received updated data from server:', updatedDataFromServer)

    if (!updatedDataFromServer || !updatedDataFromServer.id) {
      console.error('Invalid data received from server', updatedDataFromServer)
      return
    }

    // Update the specific row in orders data directly with server response
    setOrders((prevOrders) => {
      if (!prevOrders) return prevOrders

      // Create a new array to ensure React detects the change
      const updatedOrders = prevOrders.map((order) =>
        order.id === updatedDataFromServer.id
          ? {
              ...order,
              paymentStatus: updatedDataFromServer.paymentStatus,
              paPaymentDate: updatedDataFromServer.paPaymentDate,
              // paPaymentId: updatedDataFromServer.paPaymentTransactionId,
              updatedAt: updatedDataFromServer.updatedAt,
            }
          : order
      )

      return [...updatedOrders] // Return a new array reference
    })

    // Update the payment status counts
    setPaymentStatusCounts((prev) => {
      if (!selectedPayment) return prev

      const oldStatus = selectedPayment.paymentStatus
      const newStatus = updatedDataFromServer.paymentStatus

      if (oldStatus === newStatus) return prev

      // Create a new object to ensure React detects the change
      return {
        ...prev,
        [oldStatus]: Math.max(0, prev[oldStatus] - 1),
        [newStatus]: (prev[newStatus] || 0) + 1,
      }
    })

    // Show success toast
    ToastPopUp(
      toast,
      'success',
      'Success',
      'Payment status updated successfully'
    )

    // Clear selected payment
    setSelectedPayment(null)

    // Refresh the data to ensure all counts and statuses are up to date
    getData()
  }

  const handleViewPayment = (rowData) => {
    setTableLoading(true)
    server
      .get(`/admins/payables/${rowData.id}`)
      .then((response) => {
        setSelectedPayment(response.data.data)
        setPaymentModalVisible(true)
        setTableLoading(false)
      })
      .catch((error) => {
        setTableLoading(false)
        // Optionally show a toast error
      })
  }

  return (
    <MainContentLayout
      title='PA Payables per Orders'
      // dt={exportCSV}
      // handleExport={true}
      loading={csvLoading}
    >
      <Tooltip target='.p-sortable-column-icon' content='Sort' />
      <Tooltip target='.p-column-filter-menu-button' content='Filter' />
      <Toast ref={toast} />
      <Toolbar
        className='mb-2'
        left={leftToolbarTemplate}
        right={topRightToolbarTemplate}
      />
      <DataTable
        ref={dt}
        lazy
        paginatorTemplate='FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown'
        rowsPerPageOptions={rowsPerPageOptions}
        totalRecords={totalRecords}
        paginator
        value={orders}
        selectionMode='single'
        selection={selectedOrder}
        onSelectionChange={(e) => setSelectedOrder(e.value)}
        dataKey='id'
        // onRowSelect={onRowSelect}
        tableStyle={{ minWidth: '50rem' }}
        loading={tableLoading}
        filters={lazyState?.filters}
        sortField={lazyState?.sortField}
        sortOrder={lazyState?.sortOrder}
        rows={lazyState?.rows}
        first={lazyState?.first}
        onPage={onPage}
        onSort={onSort}
        onFilter={onFilter}
        removableSort
        showGridlines
      >
        <Column
          field='supplierInvoiceNo'
          header='Order No.'
          filter
          filterElement={orderNoFilterElement}
          showFilterOperator={false}
          showAddButton={false}
          showFilterMenuOptions={false}
          sortable
          footer={filteredTotals ? 'Total' : ''}
          footerStyle={{ fontWeight: 600 }}
        />
        <Column
          field='pharmacyName'
          header='Pharmacy Name'
          filter
          filterElement={pharmacyNameFilterElement}
          showFilterOperator={false}
          showAddButton={false}
          showFilterMenuOptions={false}
          sortable
        />
        <Column
          field='manufacturerName'
          header='Manufacturer'
          body={(row) => (row.manufacturerNames?.length ? row.manufacturerNames.join(', ') : '-')}
          filter
          filterElement={manufacturerFilterElement}
          showFilterOperator={false}
          showAddButton={false}
          showFilterMenuOptions={false}
          footer=""
          footerStyle={{ fontWeight: 600 }}
        />

        <Column
          field='system'
          header='Systems'
          body={systemBodyTemplate}
          sortable
          filter
          filterElement={systemFilterElement}
          showFilterOperator={false}
          showAddButton={false}
          showFilterMenuOptions={false}
        />

        <Column
          field='createdAt'
          header='Date of Confirmed Order'
          body={(payable) =>
            payable.createdAt ? formatDate(payable.createdAt) : '-'
          }
          sortable
        />
        <Column
          field='originalValue'
          header='Original Value'
          body={originalValueBodyTemplate}
          sortable
          filter
          filterElement={originalValueFilterElement}
          showFilterOperator={false}
          showAddButton={false}
          showFilterMenuOptions={false}
        />
        <Column
          field='estimatedPA'
          header='Estimated PA Amount'
          body={estimatedPABodyTemplate}
          sortable
          filter
          filterElement={estimatedPAFilterElement}
          showFilterOperator={false}
          showAddButton={false}
          showFilterMenuOptions={false}
        />
        <Column
          field='confirmedExtendTotal'
          header='Confirmed Value'
          body={confirmedValueBodyTemplate}
          sortable
          filter
          filterElement={confirmedValueFilterElement}
          showFilterOperator={false}
          showAddButton={false}
          showFilterMenuOptions={false}
          footer={filteredTotals ? formatCurrency(filteredTotals.totalConfirmedValue) : ''}
          footerStyle={{ fontWeight: 600 }}
        />
        <Column
          field='confirmedPaValue'
          header='PA Amount'
          body={paAmountBodyTemplate}
          sortable
          filter
          filterElement={paAmountFilterElement}
          showFilterOperator={false}
          showAddButton={false}
          showFilterMenuOptions={false}
          footer={filteredTotals ? formatCurrency(filteredTotals.totalPaAmount) : ''}
          footerStyle={{ fontWeight: 600 }}
        />
        <Column
          field='paymentStatus'
          header='Status'
          body={statusBodyTemplate}
          sortable
          filter
          filterElement={statusFilterElement}
          showFilterOperator={false}
          showAddButton={false}
          showFilterMenuOptions={false}
        />
        <Column
          field='paPaymentBatchId'
          header='Payment ID'
          body={(payable) =>
            payable.paymentStatus === 'Paid' && payable.paPaymentBatchId
              ? String(payable.paPaymentBatchId)
              : '-'
          }
        />
        <Column
          field='paReason'
          header='PA Cancelled Reason'
          body={(payable) => payable.paReason || '-'}
        />
      </DataTable>

      {/* Payment Transaction Modal */}
      <TransactionPopup
        visible={paymentModalVisible}
        setVisible={setPaymentModalVisible}
        paymentData={selectedPayment}
        onSave={handlePaymentSave}
        id={id}
      />
    </MainContentLayout>
  )
}
